import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography, Fade } from "@mui/material";
import { X, Search } from "lucide-react";
import { styled } from "@mui/material/styles";

const OverlayContainer = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 100,
  cursor: "crosshair",
  userSelect: "none",
  WebkitUserSelect: "none",
  touchAction: "none",
  WebkitTouchCallout: "none",
});

const FrozenFrame = styled("img")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  pointerEvents: "none",
});

const DarkOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  pointerEvents: "none",
});

const SelectionBox = styled(Box)({
  position: "absolute",
  border: "2px solid #8b5cf6",
  backgroundColor: "rgba(139, 92, 246, 0.1)",
  boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.5)",
  pointerEvents: "none",
});

const Instructions = styled(Box)({
  position: "absolute",
  top: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "rgba(139, 92, 246, 0.9)",
  color: "white",
  padding: "12px 24px",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  backdropFilter: "blur(10px)",
  boxShadow: "0 4px 20px rgba(139, 92, 246, 0.3)",
  zIndex: 101,
});

const CancelButton = styled(IconButton)({
  position: "absolute",
  top: "20px",
  right: "20px",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  color: "white",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  zIndex: 101,
});

const SizeLabel = styled(Box)({
  position: "absolute",
  bottom: "-28px",
  left: 0,
  backgroundColor: "#8b5cf6",
  color: "white",
  padding: "4px 12px",
  fontSize: "12px",
  borderRadius: "4px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  boxShadow: "0 2px 8px rgba(139, 92, 246, 0.3)",
});

interface ProductSearchOverlayProps {
  frozenFrame: string;
  onComplete: (selectedImage: string) => void;
  onCancel: () => void;
}

export const ProductSearchOverlay = ({
  frozenFrame,
  onComplete,
  onCancel,
}: ProductSearchOverlayProps) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [endPoint, setEndPoint] = useState({ x: 0, y: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);


    const getEventPos = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = overlayRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        
        // Handle both mouse and touch events
        let clientX;
        let clientY;
        if ('touches' in e) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
        } else {
        clientX = e.clientX;
        clientY = e.clientY;
        }
        
        return {
        x: clientX - rect.left,
        y: clientY - rect.top,
        };
    };

  const getMousePos = (e: React.MouseEvent) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const pos = getMousePos(e);
    setStartPoint(pos);
    setEndPoint(pos);
    setIsSelecting(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting) return;
    setEndPoint(getMousePos(e));
  };

  const handleMouseUp = () => {
    if (!isSelecting) return;
    setIsSelecting(false);
    captureSelection();
  };


  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); // Prevent default touch behavior
    const pos = getEventPos(e);
    setStartPoint(pos);
    setEndPoint(pos);
    setIsSelecting(true);
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isSelecting) return;
    setEndPoint(getEventPos(e));
  };

  const handleEnd = (e?: React.MouseEvent | React.TouchEvent) => {
    if (e) e.preventDefault();
    if (!isSelecting) return;
    setIsSelecting(false);
    captureSelection();
  };


  const captureSelection = () => {
    const left = Math.min(startPoint.x, endPoint.x);
    const top = Math.min(startPoint.y, endPoint.y);
    const width = Math.abs(endPoint.x - startPoint.x);
    const height = Math.abs(endPoint.y - startPoint.y);

    if (width < 20 || height < 20) {
      // Too small, reset
      return;
    }

    // Create canvas for selection
    const canvas = document.createElement("canvas");
    const rect = overlayRef.current?.getBoundingClientRect();
    const img = imageRef.current;
    
    if (!rect || !img) return;

    // Calculate scale factors
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;

    canvas.width = width * scaleX;
    canvas.height = height * scaleY;
    
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(
        img,
        left * scaleX,
        top * scaleY,
        width * scaleX,
        height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
      
      onComplete(canvas.toDataURL("image/png"));
    }
  };

  const selectionStyle = {
    left: Math.min(startPoint.x, endPoint.x),
    top: Math.min(startPoint.y, endPoint.y),
    width: Math.abs(endPoint.x - startPoint.x),
    height: Math.abs(endPoint.y - startPoint.y),
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  return (
    <Fade in timeout={300}>
      <OverlayContainer
        ref={overlayRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        // Prevent touch scrolling while selecting
        style={{ touchAction: 'none' }}
      >
        <FrozenFrame ref={imageRef} src={frozenFrame} alt="Frozen frame" />
        <DarkOverlay />
        
        <Instructions>
          <Search size={20} />
          <Typography variant="body2" fontWeight={500}>
            Click and drag to select a product
          </Typography>
        </Instructions>
        
        <CancelButton onClick={onCancel} size="large">
          <X />
        </CancelButton>
        
        {isSelecting && (
          <SelectionBox style={selectionStyle}>
            <SizeLabel>
              {selectionStyle.width} × {selectionStyle.height}
            </SizeLabel>
          </SelectionBox>
        )}
      </OverlayContainer>
    </Fade>
  );
};